// @owners { team: patients }
import { ActionSheetContext, ActionSheetV2, LgPadding, MdSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { format, parse } from 'date-fns';
import React, { useCallback, useContext, useEffect } from 'react';
// eslint-disable-next-line import/no-deprecated
import { validateUser } from '~shared/actions/auth';
import { clearValidatingUsersError } from '~shared/actions/uiOnboarding';
import { getCompanyName } from '~shared/config/getCompanyName';
import { VERIFY } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { DATE_FORMATS } from '~shared/helpers/date';
import { areCookiesDisabled } from '~shared/helpers/getDeviceID';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { VerifiedDetailsActionSheet } from './VerifiedDetailsActionSheet';
import { type Values as VerificationDetails, Verify } from './Verify';
import { CookieError } from '~web/features/auth/components/CookieError';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

type Props = {
  tempPassword?: string;
  useExternalVerification?: boolean;
  externalUUID?: string;
  hubReferralID?: number;
  nextURL?: string;
};

export const VerifyActionSheet = ({
  tempPassword,
  useExternalVerification,
  externalUUID,
  hubReferralID,
  nextURL,
}: Props) => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { overrideActionSheetStack, closeActionSheet } = useContext(ActionSheetContext);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const nextRoute = nextUrl({ currentRoute: VERIFY });
  const { trackPageView } = useAnalytics();
  const title =
    useExternalVerification && hubReferralID
      ? "Let's verify your identity"
      : `To claim your ${getCompanyName()} account, please verify your date of birth`;

  const onVerify = async (data: VerificationDetails) => {
    const dob = format(
      parse(data.date_of_birth, DATE_FORMATS.DATE_INPUT_FORMAT, 0),
      DATE_FORMATS.YEAR_MONTH_DAY_DASHED,
    );
    const success = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      validateUser({
        date_of_birth: dob,
        token: tempPassword,
        terms: data.terms,
        use_external_validation: useExternalVerification,
        external_uuid: externalUUID,
        hub_referral_id: hubReferralID,
      }),
    );
    if (!success) {
      return;
    }

    if (useExternalVerification) {
      if (nextURL) {
        navigate(nextURL);
        closeActionSheet();
      }
      return;
    }

    // override action sheet stack so that the new verified details sheet appears
    // first in the stack and does not show a back arrow
    overrideActionSheetStack([
      <VerifiedDetailsActionSheet
        key="verifiedDetailsSheet"
        nextUrl={nextRoute}
      />,
    ]);
  };
  const handleClose = useCallback(() => {
    dispatch(clearValidatingUsersError());
    closeActionSheet();
  }, [closeActionSheet, dispatch]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__VERIFY_ACCOUNT_VIEWED,
      params: {
        is_progyny_rx_view: showProgyny,
      },
    });
  }, [showProgyny, trackPageView]);

  const cookiesDisabled = areCookiesDisabled();

  return (
    <ActionSheetV2
      title={title}
      analyticsName={hubReferralID ? 'info request verify identity' : 'onboarding verify identity'}
      handleClose={handleClose}
    >
      <LgPadding>
        {cookiesDisabled ? (
          <>
            <CookieError />
            <MdSpacing />
          </>
        ) : null}
        <Verify
          onVerify={onVerify}
          disabled={cookiesDisabled}
          tempPassword={tempPassword}
        />
      </LgPadding>
    </ActionSheetV2>
  );
};
